'use client'

import * as React from 'react'
import dayjs from 'dayjs'

import { cn } from '../../../lib/utils'

export interface TimeInputProps {
  value: Date | undefined
  onChange: (date: Date | undefined) => void
  use12Hour?: boolean
  timeZone?: string
  disabled?: boolean
  className?: string
  label?: string
}

type TimeSegment = 'hour' | 'minute' | 'meridiem'

function TimeInput({
  value,
  onChange,
  use12Hour = true,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  disabled = false,
  className,
  label,
}: TimeInputProps) {
  const hourRef = React.useRef<HTMLInputElement>(null)
  const minuteRef = React.useRef<HTMLInputElement>(null)
  const meridiemRef = React.useRef<HTMLButtonElement>(null)

  const [focusedSegment, setFocusedSegment] =
    React.useState<TimeSegment | null>(null)
  const [hourBuffer, setHourBuffer] = React.useState('')
  const [minuteBuffer, setMinuteBuffer] = React.useState('')

  const bufferTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const clearBufferAfterDelay = React.useCallback(() => {
    if (bufferTimeoutRef.current) {
      clearTimeout(bufferTimeoutRef.current)
    }
    bufferTimeoutRef.current = setTimeout(() => {
      setHourBuffer('')
      setMinuteBuffer('')
    }, 1000)
  }, [])

  React.useEffect(() => {
    return () => {
      if (bufferTimeoutRef.current) {
        clearTimeout(bufferTimeoutRef.current)
      }
    }
  }, [])

  const currentDate = React.useMemo(() => {
    if (!value) return dayjs().tz(timeZone)
    return dayjs(value).tz(timeZone)
  }, [value, timeZone])

  const hour24 = currentDate.hour()
  const minute = currentDate.minute()
  const meridiem = hour24 >= 12 ? 'PM' : 'AM'

  const displayHour = React.useMemo(() => {
    if (!use12Hour) return hour24
    if (hour24 === 0) return 12
    if (hour24 > 12) return hour24 - 12
    return hour24
  }, [hour24, use12Hour])

  const updateTime = React.useCallback(
    (newHour24: number, newMinute: number) => {
      const base = value ? dayjs(value) : dayjs()
      const updated = base
        .hour(newHour24)
        .minute(newMinute)
        .second(0)
        .millisecond(0)
      onChange(updated.toDate())
    },
    [value, onChange],
  )

  const handleHourChange = React.useCallback(
    (newDisplayHour: number) => {
      let newHour24: number
      if (use12Hour) {
        if (meridiem === 'AM') {
          newHour24 = newDisplayHour === 12 ? 0 : newDisplayHour
        } else {
          newHour24 = newDisplayHour === 12 ? 12 : newDisplayHour + 12
        }
      } else {
        newHour24 = newDisplayHour
      }
      updateTime(newHour24, minute)
    },
    [use12Hour, meridiem, minute, updateTime],
  )

  const handleMinuteChange = React.useCallback(
    (newMinute: number) => {
      updateTime(hour24, newMinute)
    },
    [hour24, updateTime],
  )

  const handleMeridiemToggle = React.useCallback(() => {
    const newHour24 = meridiem === 'AM' ? hour24 + 12 : hour24 - 12
    updateTime(Math.max(0, Math.min(23, newHour24)), minute)
  }, [meridiem, hour24, minute, updateTime])

  const incrementHour = React.useCallback(
    (delta: number) => {
      const maxHour = use12Hour ? 12 : 23
      const minHour = use12Hour ? 1 : 0
      let newHour = displayHour + delta
      if (newHour > maxHour) newHour = minHour
      if (newHour < minHour) newHour = maxHour
      handleHourChange(newHour)
    },
    [displayHour, use12Hour, handleHourChange],
  )

  const incrementMinute = React.useCallback(
    (delta: number) => {
      let newMinute = minute + delta
      if (newMinute >= 60) newMinute = 0
      if (newMinute < 0) newMinute = 59
      handleMinuteChange(newMinute)
    },
    [minute, handleMinuteChange],
  )

  const handleHourKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault()
          incrementHour(1)
          break
        case 'ArrowDown':
          e.preventDefault()
          incrementHour(-1)
          break
        case 'ArrowRight':
        case 'Tab':
          if (!e.shiftKey) {
            e.preventDefault()
            minuteRef.current?.focus()
            minuteRef.current?.select()
          }
          break
        case 'ArrowLeft':
          break
        default:
          if (/^[0-9]$/.test(e.key)) {
            e.preventDefault()
            const newBuffer = hourBuffer + e.key
            const numValue = parseInt(newBuffer, 10)
            const maxHour = use12Hour ? 12 : 23
            const minHour = use12Hour ? 1 : 0

            if (newBuffer.length === 1) {
              if (use12Hour && numValue > 1) {
                handleHourChange(numValue)
                setHourBuffer('')
                minuteRef.current?.focus()
                minuteRef.current?.select()
              } else if (!use12Hour && numValue > 2) {
                handleHourChange(numValue)
                setHourBuffer('')
                minuteRef.current?.focus()
                minuteRef.current?.select()
              } else {
                setHourBuffer(newBuffer)
                clearBufferAfterDelay()
              }
            } else {
              const clampedValue = Math.max(
                minHour,
                Math.min(maxHour, numValue),
              )
              handleHourChange(clampedValue)
              setHourBuffer('')
              minuteRef.current?.focus()
              minuteRef.current?.select()
            }
          }
          break
      }
    },
    [
      disabled,
      incrementHour,
      hourBuffer,
      use12Hour,
      handleHourChange,
      clearBufferAfterDelay,
    ],
  )

  const handleMinuteKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault()
          incrementMinute(1)
          break
        case 'ArrowDown':
          e.preventDefault()
          incrementMinute(-1)
          break
        case 'ArrowRight':
        case 'Tab':
          if (!e.shiftKey && use12Hour) {
            e.preventDefault()
            meridiemRef.current?.focus()
          }
          break
        case 'ArrowLeft':
        case 'Backspace':
          if (
            e.key === 'ArrowLeft' ||
            (e.key === 'Backspace' && minuteBuffer === '')
          ) {
            e.preventDefault()
            hourRef.current?.focus()
            hourRef.current?.select()
          }
          break
        default:
          if (/^[0-9]$/.test(e.key)) {
            e.preventDefault()
            const newBuffer = minuteBuffer + e.key
            const numValue = parseInt(newBuffer, 10)

            if (newBuffer.length === 1) {
              if (numValue > 5) {
                handleMinuteChange(numValue)
                setMinuteBuffer('')
                if (use12Hour) {
                  meridiemRef.current?.focus()
                }
              } else {
                setMinuteBuffer(newBuffer)
                clearBufferAfterDelay()
              }
            } else {
              const clampedValue = Math.min(59, numValue)
              handleMinuteChange(clampedValue)
              setMinuteBuffer('')
              if (use12Hour) {
                meridiemRef.current?.focus()
              }
            }
          }
          break
      }
    },
    [
      disabled,
      incrementMinute,
      minuteBuffer,
      use12Hour,
      handleMinuteChange,
      clearBufferAfterDelay,
    ],
  )

  const handleMeridiemKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return

      switch (e.key) {
        case 'ArrowUp':
        case 'ArrowDown':
        case ' ':
        case 'Enter':
          e.preventDefault()
          handleMeridiemToggle()
          break
        case 'ArrowLeft':
        case 'Backspace':
          e.preventDefault()
          minuteRef.current?.focus()
          minuteRef.current?.select()
          break
        case 'a':
        case 'A':
          e.preventDefault()
          if (meridiem === 'PM') handleMeridiemToggle()
          break
        case 'p':
        case 'P':
          e.preventDefault()
          if (meridiem === 'AM') handleMeridiemToggle()
          break
        case 'Tab':
          if (e.shiftKey) {
            e.preventDefault()
            minuteRef.current?.focus()
            minuteRef.current?.select()
          }
          break
      }
    },
    [disabled, handleMeridiemToggle, meridiem],
  )

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && <span className="text-sm text-muted-foreground">{label}</span>}
      <div className="flex items-center gap-1">
        <div
          className={cn(
            'flex items-center rounded-md border border-input bg-transparent px-2 py-1',
            'focus-within:ring-1 focus-within:ring-ring',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <input
            ref={hourRef}
            type="text"
            inputMode="numeric"
            value={displayHour.toString().padStart(2, '0')}
            readOnly
            disabled={disabled}
            onFocus={() => {
              setFocusedSegment('hour')
              hourRef.current?.select()
            }}
            onBlur={() => setFocusedSegment(null)}
            onKeyDown={handleHourKeyDown}
            className={cn(
              'w-6 bg-transparent text-center text-sm tabular-nums outline-none',
              'selection:bg-primary selection:text-primary-foreground',
              disabled && 'cursor-not-allowed',
            )}
            aria-label="Hour"
          />
          <span className="text-muted-foreground">:</span>
          <input
            ref={minuteRef}
            type="text"
            inputMode="numeric"
            value={minute.toString().padStart(2, '0')}
            readOnly
            disabled={disabled}
            onFocus={() => {
              setFocusedSegment('minute')
              minuteRef.current?.select()
            }}
            onBlur={() => setFocusedSegment(null)}
            onKeyDown={handleMinuteKeyDown}
            className={cn(
              'w-6 bg-transparent text-center text-sm tabular-nums outline-none',
              'selection:bg-primary selection:text-primary-foreground',
              disabled && 'cursor-not-allowed',
            )}
            aria-label="Minute"
          />
        </div>

        {use12Hour && (
          <button
            ref={meridiemRef}
            type="button"
            disabled={disabled}
            onClick={handleMeridiemToggle}
            onFocus={() => setFocusedSegment('meridiem')}
            onBlur={() => setFocusedSegment(null)}
            onKeyDown={handleMeridiemKeyDown}
            className={cn(
              'flex h-8 w-10 items-center justify-center rounded-md border border-input bg-transparent text-sm font-medium',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:outline-none focus:ring-1 focus:ring-ring',
              disabled && 'cursor-not-allowed opacity-50',
            )}
            aria-label={`Meridiem, currently ${meridiem}. Press to toggle.`}
          >
            {meridiem}
          </button>
        )}
      </div>
    </div>
  )
}

TimeInput.displayName = 'TimeInput'

export { TimeInput }
